import { useCallback, useEffect, useState } from 'react';
import {
  HARDWARE_PHOTO_CONTENT_TYPES,
  HARDWARE_PHOTO_MAX_BYTES,
  type HardwarePhoto,
  type HardwarePhotoContentType,
  type HardwarePhotoUpload,
} from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

function isAllowedType(type: string): type is HardwarePhotoContentType {
  return (HARDWARE_PHOTO_CONTENT_TYPES as readonly string[]).includes(type);
}

/**
 * Photos d'un build (ADR-0032). Le dépôt suit le motif pré-signé des images (ADR-0010) : l'API
 * réserve la ligne et renvoie une URL, le navigateur **dépose l'octet droit sur le stockage** (il ne
 * traverse pas l'API), puis l'API confirme. La taille/le type sont bornés (liste blanche + 8 Mo).
 */
export function useHardwarePhotos(assetId: string | null) {
  const [photos, setPhotos] = useState<HardwarePhoto[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      setPhotos(await apiFetch<HardwarePhoto[]>(`/hardware/${assetId}/photos`));
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Dépose une photo. Rejette (message i18n via clé) si le type ou la taille sont hors gabarit. */
  const upload = useCallback(
    async (file: File): Promise<void> => {
      if (!assetId) return;
      if (!isAllowedType(file.type)) throw new Error('hw.photos.badType');
      if (file.size > HARDWARE_PHOTO_MAX_BYTES) throw new Error('hw.photos.tooBig');

      const reserved = await apiFetch<HardwarePhotoUpload>(`/hardware/${assetId}/photos`, {
        method: 'POST',
        body: { contentType: file.type },
      });
      // Dépôt direct sur le stockage — l'octet ne passe pas par l'API (CLAUDE.md §2).
      const put = await fetch(reserved.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!put.ok) throw new Error('hw.photos.uploadFailed');
      await apiFetch(`/hardware/${assetId}/photos/${reserved.id}/confirm`, { method: 'POST' });
      await refresh();
    },
    [assetId, refresh],
  );

  const remove = useCallback(
    async (photoId: string): Promise<void> => {
      if (!assetId) return;
      await apiFetch(`/hardware/${assetId}/photos/${photoId}`, { method: 'DELETE' });
      await refresh();
    },
    [assetId, refresh],
  );

  return { photos, loading, upload, remove };
}
